import Cookies from "js-cookie";
import useSWR from "swr";

import {ApiError, apiFetcher, friendlyErrorMessage, unwrapEnvelope} from "@lib/fetcher";

// ── Types ─────────────────────────────────────────────────────

export interface ForecastOp {
    id: string;
    op_type: "set_baseline" | "set_driver" | "set_override";
    target_type: "category" | "row" | "cell";
    target_id: string;
    label: string;
    params: Record<string, unknown>;
    ordinal: number;
    is_active: boolean;
    created_by: "human" | "agent";
    created_at: string;
}

export interface ForecastScenario {
    id: string;
    name: string;
    is_default: boolean;
    baseline_method: string;
    created_at: string;
    updated_at: string;
    ops_count?: number;
}

export interface BaselineMethod {
    key: string;
    label: string;
    trailing: number;
}

export interface ForecastRow {
    name: string;
    amount: number;
    account_id?: string | null;
}

export interface ForecastSection {
    title: string;
    type: "income" | "cogs" | "expense" | "other";
    rows: ForecastRow[];
    total: number;
}

export interface ForecastMonth {
    month: string;
    label: string;
    status: "actual" | "forecast";
    confidence?: "high" | "medium" | "low";
    sections?: ForecastSection[];
    revenue: number;
    cogs: number;
    grossProfit: number;
    expenses: number;
    netProfit: number;
}

export interface CellProvenanceItem {
    value: string;
    baseline: string;
    is_override: boolean;
    ops_applied: {
        op_id: string;
        label: string;
        op_type: string;
        driver_type?: string;
        value: string;
        effect: string;
    }[];
    calculation: string;
}

export interface ForecastV2Data {
    connected: boolean;
    message?: string;
    error?: string;
    scenario: ForecastScenario;
    baseline: { revenue: number; cogs: number; expenses: number };
    months: ForecastMonth[];
    cell_provenance: Record<string, CellProvenanceItem>;
    ops_count: number;
}

interface SummaryDelta {
    before: string;
    after: string;
    delta: string;
}

interface CellDelta {
    cell: string;
    row_name: string;
    month_label: string;
    before: string;
    after: string;
    delta: string;
    delta_pct: string | null;
}

export interface PreviewResult {
    deltas: CellDelta[];
    summary_deltas: Record<string, SummaryDelta>;
    after: {
        months: ForecastMonth[];
        baseline: { revenue: number; cogs: number; expenses: number };
        cell_provenance: Record<string, CellProvenanceItem>;
    };
}

export interface OpInput {
    op_type: "set_baseline" | "set_driver" | "set_override";
    target_type: "category" | "row" | "cell";
    target_id: string;
    label?: string;
    params: Record<string, unknown>;
}

// ── Fetcher ────────────────────────────────────────────────────
// GET requests use the shared apiFetcher (timeout + retry + envelope unwrap).

const MUTATION_TIMEOUT_MS = 60_000;

const postFetcher = async <T>(url: string, body: unknown): Promise<T> => {
    const csrfToken = Cookies.get("csrftoken") || "";
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify(body),
            signal: controller.signal,
        });
        clearTimeout(timer);
        if (!response.ok) {
            let msg: string | undefined;
            try {
                const data = await response.json();
                msg = data.error || data.errors?.[0] || undefined;
            } catch { /* ignore */ }
            const kind = response.status === 401 ? "auth"
                : response.status === 403 ? "permission"
                : response.status >= 500 ? "server" : "unknown";
            if (response.status === 401) msg = "Session expired — please log in again.";
            if (response.status === 403) msg = "Permission denied — your Xero connection may have expired.";
            throw new ApiError(msg || `Request failed (${response.status})`, kind, response.status);
        }
        const json = await response.json();
        return unwrapEnvelope<T>(json);
    } catch (err) {
        clearTimeout(timer);
        if (err instanceof ApiError) throw err;
        if (err instanceof Error && err.name === "AbortError") {
            throw new ApiError("Request timed out — please try again.", "network");
        }
        throw new ApiError("Network error — check your connection and try again.", "network");
    }
};

// ── SWR Hooks ──────────────────────────────────────────────────

export function useForecastV2(scenarioId?: string, months = 6) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = `/api/forecast/v2/?${params}`;

    const {data, error, isLoading, isValidating, mutate} = useSWR<ForecastV2Data>(url, apiFetcher, {
        
        dedupingInterval: 60000,
        errorRetryCount: 3,
        keepPreviousData: true,
    });

    const errorKind = error instanceof ApiError ? error.kind : error ? "unknown" as const : null;
    // Suppress transient errors while revalidating if we already have data
    const showError = error && !(isValidating && data);

    return {
        data,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        errorKind: showError ? errorKind : null,
        isConnected: data?.connected ?? false,
        refresh: mutate,
    };
}

export function useOps(scenarioId?: string) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = `/api/forecast/v2/ops/?${params}`;

    const {data, error, isLoading, mutate} = useSWR<{scenario_id: string; ops: ForecastOp[]}>(
        url, apiFetcher, {dedupingInterval: 10000}
    );

    return {ops: data?.ops ?? [], isLoading, error, refresh: mutate};
}

export function useScenarios() {
    const {data, error, isLoading, mutate} = useSWR<{scenarios: ForecastScenario[]}>(
        "/api/forecast/v2/scenarios/", apiFetcher, {dedupingInterval: 30000}
    );

    return {scenarios: data?.scenarios ?? [], isLoading, error, refresh: mutate};
}

export function useBaselineMethods() {
    const {data} = useSWR<{methods: BaselineMethod[]}>(
        "/api/forecast/v2/baseline-methods/", apiFetcher, {dedupingInterval: 300000}
    );
    return data?.methods ?? [];
}

// ── Mutations ──────────────────────────────────────────────────

export async function previewOps(ops: OpInput[], scenarioId?: string, months = 6): Promise<PreviewResult> {
    return postFetcher<PreviewResult>("/api/forecast/v2/preview/", {
        ops,
        scenario_id: scenarioId,
        months,
    });
}

export async function commitOps(ops: OpInput[], scenarioId?: string, months = 6) {
    return postFetcher<{committed_ops: ForecastOp[]; scenario: ForecastScenario; months: ForecastMonth[];}>(
        "/api/forecast/v2/commit/",
        {ops, scenario_id: scenarioId, months, created_by: "human"},
    );
}

export async function deleteOp(opId: string): Promise<void> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
    try {
        const response = await fetch(`/api/forecast/v2/ops/${opId}/`, {
            method: "DELETE",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            signal: controller.signal,
        });
        clearTimeout(timer);
        if (!response.ok) {
            const kind = response.status === 401 ? "auth"
                : response.status === 403 ? "permission"
                : response.status >= 500 ? "server" : "unknown";
            throw new ApiError(`Failed to delete op (${response.status})`, kind, response.status);
        }
    } catch (err) {
        clearTimeout(timer);
        if (err instanceof ApiError) throw err;
        if (err instanceof Error && err.name === "AbortError") {
            throw new ApiError("Request timed out — please try again.", "network");
        }
        throw new ApiError("Network error — check your connection and try again.", "network");
    }
}

export async function createScenario(
    name: string,
    baselineMethod = "avg_3m",
    copyFrom?: string,
): Promise<ForecastScenario> {
    const result = await postFetcher<ForecastScenario>("/api/forecast/v2/scenarios/", {
        name,
        baseline_method: baselineMethod,
        copy_from: copyFrom,
    });
    return result;
}
